import { message } from 'antd';
import isEqual from 'lodash.isequal';
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import { DEFAULT_TEXT_CONFIG } from 'config/texts';
import { FormFieldEntityApi } from 'entities/FormField';
import { FormStatus } from 'entities/common';
import {
  FormGetRequestApi,
  FormGetResponseApi,
} from 'entities/endpoints/form.get_form';
import { FormUpdateRequestApi } from 'entities/endpoints/form.update_form';
import { ApiRequest, Collection } from 'stores/common';
import { ILocalStore, IServerEntity } from 'stores/interfaces';
import { EntryText } from 'stores/models';
import { printErrors } from 'utils/printErrors';
import { validateNotEmpty } from 'utils/validators';

import { EDIT_FORM_TEXTS } from '../EditFormStore/texts';
import { FormFieldModel } from '../models';

import { CatalogFieldsStore } from './CatalogFieldsStore';

type PrivateFields =
  | '_id'
  | '_status'
  | '_prevData'
  | '_hasFieldsWithoutName'
  | '_setNewCollection';

/**
 * Базовый стор который хранит инфу о полях формы
 */
export class BasicFormStore
  implements ILocalStore, IServerEntity<FormUpdateRequestApi>
{
  constructor() {
    makeObservable<BasicFormStore, PrivateFields>(this, {
      fieldsCollection: observable.ref,

      handleCustomFieldAdd: action.bound,

      _id: observable,
      id: computed,

      _status: observable.ref,
      status: computed,

      _prevData: observable.ref,
      prevData: computed,

      toServerFormat: computed,
      isDataChanged: computed,
      hasData: computed,
      hasCustomFields: computed,
      _hasFieldsWithoutName: computed,

      loadingEditTable: computed,

      fetchFormById: action.bound,
      updateFormStatus: action.bound,
      saveEditFormStage: action.bound,
      saveFormName: action.bound,

      initialize: action.bound,
      _setNewCollection: action.bound,
    });

    this._reactions.push(
      reaction(
        () => this._status,
        async (newStatus) => {
          if (newStatus === FormStatus.DRAFT) {
            /* Подгружаем поля для отображения первого этапа */
            await this._init();
          }
        }
      )
    );
  }

  private _customizableFields: FormFieldEntityApi[] = [];

  /**
   * Предыдущие данные
   * @private
   */
  private _prevData:
    | (FormGetResponseApi & { customizableFields: FormFieldEntityApi[] })
    | null = null;

  /**
   * Статус формы
   * @private
   */
  private _status: FormStatus | null = null;

  private readonly _reactions: IReactionDisposer[] = [];

  private _id: number | null = null;

  private _startTime: string | null = null;

  private _finishTime: string | null = null;

  readonly catalogStore = new CatalogFieldsStore();

  readonly formUpdateRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.update,
    methodType: 'post',
  });

  /* todo: Зарефачить BasicStore, вынеся все поля и логику их обработки в EditFormStore */
  readonly fieldsCollection = new Collection(
    FormFieldModel.createFromJSON,
    (entity: FormFieldModel) => entity.id
  );

  readonly formGetRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.get,
    methodType: 'get',
  });

  /**
   * Имя таблицы
   */
  readonly formName = new EntryText({
    required: true,
    validator: validateNotEmpty(),
    defaultValue: DEFAULT_TEXT_CONFIG.FORM_CREATE_NAME_DEFAULT,
  });

  readonly formCreateRequest = new ApiRequest({
    methodType: 'POST',
    endpoint: ENDPOINTS.form.create,
  });

  readonly formGetFieldsRequest = new ApiRequest({
    methodType: 'GET',
    endpoint: ENDPOINTS.form.formFields,
  });

  /**
   * Данные для обновления формы
   * todo: Вынести в EditFormStore
   */
  get toServerFormat(): FormUpdateRequestApi {
    return {
      status: this.status as FormStatus,
      name: this.formName.value,
      id: this.id as number,
      customizable_fields: this.isDataChanged
        ? this.fieldsCollection.linearizeCollection(
            (elem) => elem.toServerFormat
          )
        : undefined,
    };
  }

  /* todo: Вынести в EditFormStore*/
  get loadingEditTable() {
    return (
      this.catalogStore.fetchFormDataRequest.loading ||
      this.formGetFieldsRequest.loading
    );
  }

  /**
   * Есть ли уже запрошенная дата (помогает не запрашивать данные после create)
   *  todo: Вынести в EditFormStore
   */
  get hasData(): boolean {
    return !this.fieldsCollection.isEmpty;
  }

  /* todo: Вынести в EditFormStore*/
  get hasCustomFields(): boolean {
    return this.fieldsCollection
      .linearizeCollection((item) => item)
      .some((item) => item.custom);
  }

  /* todo: Вынести в EditFormStore*/
  get saved(): boolean {
    return !this.fieldsCollection
      .linearizeCollection((item) => item)
      .some((item) => !item.saved);
  }

  /* todo: Вынести в EditFormStore*/
  get isDataChanged(): boolean {
    return !isEqual(
      this._customizableFields.map((item) => {
        // @ts-ignore todo ( убрать костыль после фикса на бэке )
        delete item.priority;

        return item;
      }),
      this.fieldsCollection.linearizeCollection((elem) => elem.toServerFormat)
    );
  }

  /* todo: Вынести в EditFormStore*/
  private get _hasFieldsWithoutName(): boolean {
    return this.fieldsCollection
      .linearizeCollection((item) => item)
      .some((item) => {
        return item.custom && item.nameModel.value.trim() === '';
      });
  }

  /**
   * Возвращает предыдущие данные
   * todo: Вынести в EditFormStore
   */
  get prevData(): FormUpdateRequestApi | null {
    return this._prevData;
  }

  /* todo: Вынести в EditFormStore*/
  get isAlreadySubmittedForm(): boolean {
    return Boolean(this._startTime) || Boolean(this._finishTime);
  }

  get id(): number | null {
    return this._id;
  }

  get status(): FormStatus | null {
    return this._status;
  }

  /* todo: go private todo: Вынести в EditFormStore */
  private _setNewCollection(newData: FormFieldEntityApi[]): void {
    this._customizableFields = newData;
    this.fieldsCollection.parseCollection(newData);
  }

  destroy() {
    this._reactions.forEach((reaction) => reaction());
    this.fieldsCollection.destroy();
    this.formName.destroy();

    this.formGetRequest.destroy();
    this.formUpdateRequest.destroy();
  }

  /* todo: Вынести в EditFormStore*/
  private async _init(): Promise<void> {
    await this.catalogStore.fetchData();
    await this._fetchFormFields();
  }

  /* Подгружаем поля для таблицы на первом этапе
   * todo: Вынести в EditFormStore */
  private async _fetchFormFields(): Promise<void> {
    const { isError, data } = await this.formGetFieldsRequest.call<
      FormFieldEntityApi[]
    >({
      data: {
        id: this.id,
      },
    });

    if (isError || !data) {
      return;
    }

    const parsed = data.filter((item) => {
      return Boolean(this.catalogStore.fieldsCollection[item.field_data.id]);
    });

    runInAction(() => {
      if (this._prevData?.id) {
        this._prevData = {
          ...this._prevData,
          customizableFields: parsed,
        };
      }

      this._setNewCollection(parsed);
    });
  }

  /**
   * Инициализация
   * @param data
   */
  initialize(data: FormGetResponseApi): void {
    this.formName.setValue(data.name);
    this._id = data.id;
    this._status = data.status;
    this._finishTime = data.finish_datetime;
    this._startTime = data.start_datetime;
    this._prevData = {
      ...data,
      customizableFields: [],
    };
  }

  reset(): void {
    if (!this._prevData?.customizableFields) {
      return;
    }

    this._setNewCollection(this._prevData.customizableFields);
  }

  /* todo: Вынести в EditFormStore */
  handleCustomFieldAdd(): void {
    this.fieldsCollection.rebuildCollection(
      this.fieldsCollection
        .linearizeCollection((item) => item)
        .concat(FormFieldModel.createCustomField(this.id as number))
    );
  }

  /**
   * Запрос за данными формы (form_get)
   * @param id
   */
  async fetchFormById(id: number): Promise<void> {
    if (!id) {
      return;
    }

    try {
      const { data, isError } = await this.formGetRequest.call<
        FormGetResponseApi,
        FormGetRequestApi
      >({
        data: {
          id,
          with_fields: false,
        } /* todo: выпиливаем это поле на бэке */,
      });

      if (data && !isError) {
        this.initialize(data);

        /* Обновляем статус формы, чтобы бэк корректно отправлял данные для таблицы*/
        if (
          data.status === FormStatus.FINISHED ||
          data.status === FormStatus.PUBLISHED
        ) {
          await this.updateFormStatus(FormStatus.VALIDATION);
          await this.fetchFormById(id);
        }
      }
    } catch (e) {
      printErrors(e);
    }
  }

  async publishForm({
    start,
    end,
  }: {
    start: string | null;
    end: string | null;
  }): Promise<boolean> {
    const { isError } = await this.formUpdateRequest.call<
      FormGetResponseApi,
      FormUpdateRequestApi
    >({
      data: {
        id: this.id as number,
        status: FormStatus.PUBLISHED,
        start_datetime: start,
        finish_datetime: end,
      },
    });

    return isError;
  }

  /**
   * создание формы
   */
  async createForm(): Promise<void> {
    try {
      const { data } = await this.formCreateRequest.call<FormGetResponseApi>({
        data: { name: DEFAULT_TEXT_CONFIG.FORM_CREATE_NAME_DEFAULT },
      });

      if (data) {
        await this.catalogStore.fetchData();
        this.initialize(data);
      }
    } catch (err) {
      printErrors(err);
    }
  }

  /* todo: Вынести в EditFormStore */
  validate(): void {
    this.fieldsCollection.linearizeCollection((item) => item.nameModel.touch());
  }

  async saveFormName(): Promise<void> {
    await this.formUpdateRequest.call<FormGetResponseApi, FormUpdateRequestApi>(
      {
        data: {
          name: this.formName.value,
          id: this.toServerFormat.id,
          status: this.toServerFormat.status,
        },
      }
    );

    runInAction(() => {
      if (this._prevData?.id) {
        this._prevData = {
          ...this._prevData,
          name: this.formName.value,
        };
      }
    });
  }

  async updateFormStatus(nextStatus: FormStatus): Promise<void> {
    const { isError, data } = await this.formUpdateRequest.call<
      FormGetResponseApi,
      FormUpdateRequestApi
    >({
      data: {
        id: this.toServerFormat.id,
        status: nextStatus,
      },
    });

    if (isError || !data) {
      return;
    }

    this._status = data.status;
  }

  /* todo: Вынести в EditFormStore*/
  async saveEditFormStage(): Promise<void> {
    this.validate();

    if (this._hasFieldsWithoutName) {
      message.error(EDIT_FORM_TEXTS.ALL_FIELDS_MUST_HAVE_NAME);

      return;
    }

    const { isError, data } = await this.formUpdateRequest.call<
      FormGetResponseApi,
      FormUpdateRequestApi
    >({
      data: {
        ...this.toServerFormat,
        status: FormStatus.DRAFT,
      },
    });

    if (isError || !data) {
      message.error(EDIT_FORM_TEXTS.UPDATE_ERROR);

      return;
    }

    /** Подгружаем каталог заново, если сохраняли кастомные поля, так как нам необходимо правильно замапить структуру с бэка */
    if (this.hasCustomFields) {
      await this.catalogStore.fetchData();
    }

    const parsed = data.customizable_fields.filter((item) => {
      return Boolean(this.catalogStore.fieldsCollection[item.field_data.id]);
    });

    runInAction(() => {
      if (this._prevData) {
        this._prevData = {
          ...this._prevData,
          customizableFields: parsed,
        };
      }
    });

    this._setNewCollection(parsed);

    message.success(EDIT_FORM_TEXTS.UPDATE_SUCCESS);
  }
}
